import { ArrowRightIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Banner, Button, ButtonGroup, TextButton, useBreakpoint } from 'slab'
import { getEnglishLanguageDateFromDateString } from 'utils'
import { usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdSelectedPlansCreateStraightRenewalSelectedPlans as usePostStraightRenewal } from 'utils/web_api'
import {
  type MemberOnboardingMemberPlanChangesProps,
  type MemberOnboardingRestrictions,
} from 'utils/web_api.schemas'

import QLEShowCurrentPlan from './QLEShowCurrentPlan'
import BudgetAndPlanChangesSection, {
  MonthlyAllowanceSection,
} from '../components/BudgetAndPlanChangesSection/BudgetAndPlanChangesSection'
import { OnboardingGenericErrorMessage } from '../components/OnboardingGenericErrorMessage'
import { PlanDetailsSection } from '../components/PlanDetailsSection/PlanDetailsSection'
import { QLEButtonLayout } from '../components/QLEButtonLayout'
import { useOnboarding } from '../OnboardingContext'
import { type OnboardingInfo, type StepComponentProps } from '../types'
import { getAddedParticipantNames, getRemovedParticipantNames, getSummaryTitleAndDescription, isCurrentPlanOffThatch, isEnrollmentEventRenewal, isNewQleFlow, shouldShowQleCurrentPlan } from '../utils'

/**
 * This function returns a message to be used for the medical plan detail comparison table
 * in case the plan for the next year is not available. There will be two cases:
 * - next year's plan is not available due to the address change (and not due to medicare)
 * - next year's plan is not available due to other reason
 *  (ex/ they have to go through medicare, the plan simply doesn't exist, etc)
 *
 * @param addressHasChanged - whether the address has changed for the member
 * @param showGoThroughMedicareWarning - if the user actually needs to go through medicare
 *    (in that case they would need to go through medicare instead of choosing a new plan,
 *    and in this case this param will be true)
 * @param previousPlanUnavailable - this is true when the user has to go through medicare
 *  or if the plan doesn't exist for the next year
 * @param planChangeSummary - the plan change summary from the API - this includes the current and next year's plans
 */
export const getMedicalPlanNoPlanMessage = ({
  addressHasChanged,
  previousPlanUnavailable,
  planChangeSummary,
  showGoThroughMedicareWarning,
}: {
  addressHasChanged: boolean;
  previousPlanUnavailable: boolean;
  planChangeSummary?: MemberOnboardingMemberPlanChangesProps;
  showGoThroughMedicareWarning?: boolean;
}) => {
  const currentPlanYearLabel =
    planChangeSummary?.current_plan_year?.plan_year || 'current'
  const nextPlanYearLabel =
    planChangeSummary?.next_plan_year?.plan_year || 'upcoming year'

  if (
    addressHasChanged &&
    previousPlanUnavailable &&
    !showGoThroughMedicareWarning
  ) {
    return `Your ${currentPlanYearLabel} plan isn't available for ${nextPlanYearLabel} at your new address`
  } else if (previousPlanUnavailable) {
    return `Your ${currentPlanYearLabel} plan isn't available for ${nextPlanYearLabel}`
  }
  return undefined
}

/**
 * This function returns a message to be used for the warning banner to let users to know
 * to choose plans due to missing plans for the next year. There can be multiple plans missing
 * or the plan is missing due to the address change (for medical) so we want to clarify that in the message.
 *
 * @param addressHasChanged - whether the address has changed for the member
 * @param onboardingData - the onboarding data from the API - this includes the enrollment event end date
 * @param planChangeSummary - the plan change summary from the API - this includes the current and next year's plans
 */
export const getPreviousPlanArentAvailableMessage = ({
  addressHasChanged,
  onboardingData,
  planChangeSummary,
}: {
  addressHasChanged: boolean;
  onboardingData?: OnboardingInfo | null;
  planChangeSummary?: MemberOnboardingMemberPlanChangesProps;
}) => {
  const nextPlanYear = planChangeSummary?.next_plan_year
  const nextMedicalPlan = nextPlanYear?.medical_plan
  const nextDentalPlan = nextPlanYear?.dental_plan
  const nextVisionPlan = nextPlanYear?.vision_plan

  let noPlanCount = 0
  if (!nextMedicalPlan) {
    noPlanCount += 1
  }
  if (!nextDentalPlan) {
    noPlanCount += 1
  }
  if (!nextVisionPlan) {
    noPlanCount += 1
  }

  const previousPlanText =
    noPlanCount > 1
      ? "Your previous plans aren't"
      : "One of your previous plans isn't"
  const selectPlanText = noPlanCount > 1 ? 'new plans' : 'a new plan'

  return `${previousPlanText} available${addressHasChanged ? '  at your new address.' : '.'
    }
  Select ${selectPlanText} by ${getEnglishLanguageDateFromDateString(
      onboardingData?.enrollment_event?.end_date || ''
    )} to prevent a lapse in coverage.`
}

function getScrollParent (element: HTMLElement | null) {
  if (!element) return null

  if (element.scrollHeight > element.clientHeight) {
    return element
  } else {
    return getScrollParent(element.parentElement)
  }
}

function useScrollToTop (ref: React.RefObject<HTMLDivElement>) {
  const scrollToTop = useCallback(() => {
    if (ref.current) {
      const scrollParent = getScrollParent(ref.current)

      if (scrollParent) {
        scrollParent.scrollTo({
          top: 0,
        })
      }
    }
  }, [])

  return scrollToTop
}

export function RenewalShowSummaryPage ({
  navigateToNextStep,
  navigateToPreviousStep,
  navigateToSpecificStep,
}: StepComponentProps) {
  const {
    enrollmentEventId,
    isPlanChangeSummaryLoading,
    planChangeSummary,
    onboardingData,
    employerName,
    refreshOnboardingInfo,
    isMemberOnTieredAllowance,
    newQleEnabled,
  } = useOnboarding()
  const { breakpointIsAtLeast } = useBreakpoint()
  const [showPlanDetails, setShowPlanDetails] = useState(false)
  const [submitError, setSubmitError] = useState<React.ReactNode | null>(null)

  const qmeAllowed = onboardingData?.allowance?.qme_treatment === 'all_allowed'
  const hasFamily =
    (onboardingData?.additional_participants &&
      onboardingData?.additional_participants?.length > 0) ||
    false

  const isDesktop = breakpointIsAtLeast('xs')

  const isQLEFlow = onboardingData && isNewQleFlow(onboardingData, newQleEnabled)

  const pageRef = useRef<HTMLDivElement>(null)
  const scrollToTop = useScrollToTop(pageRef)

  useEffect(() => {
    scrollToTop()
  }, [showPlanDetails])

  const isCurrentPlanOffThatchMedical =
    planChangeSummary?.current_plan_year.medical_plan?.category ===
    'off_thatch_medical' || false

  const isPlanOffThatchOrMedicare = planChangeSummary
    ? isCurrentPlanOffThatch(planChangeSummary)
    : false

  // if the current plan cannot support the member's county, that means that the
  // county has been changed due to the address change and the plan has been filtered out.
  const addressHasChanged =
    planChangeSummary?.current_plan_year?.medical_plan
      ?.can_support_member_county === false || false
  const nextYearMedicalDoesNotExist =
    !planChangeSummary?.next_plan_year?.medical_plan
  const previousPlanUnavailable =
    !isPlanOffThatchOrMedicare && nextYearMedicalDoesNotExist

  const isMedicareEligible =
    onboardingData?.restrictions?.medicare_eligible &&
    onboardingData?.restrictions?.medicare_eligible?.length > 0

  const showGoThroughMedicareWarning =
    isMedicareEligible && !isPlanOffThatchOrMedicare
  const currentDentalIsNotAvailableForNextYear =
    planChangeSummary &&
    planChangeSummary.current_plan_year &&
    planChangeSummary.current_plan_year.dental_plan &&
    planChangeSummary.next_plan_year &&
    !planChangeSummary.next_plan_year.dental_plan
  const currentVisionIsNotAvailableForNextYear =
    planChangeSummary &&
    planChangeSummary.current_plan_year &&
    planChangeSummary.current_plan_year.vision_plan &&
    planChangeSummary.next_plan_year &&
    !planChangeSummary.next_plan_year.vision_plan

  const medicalPlanNoPlanMessage = getMedicalPlanNoPlanMessage({
    addressHasChanged,
    previousPlanUnavailable,
    showGoThroughMedicareWarning,
    planChangeSummary,
  })

  const shouldPlanDetailsBeVisible = showPlanDetails || previousPlanUnavailable

  const createStraightRenewal = usePostStraightRenewal()
  const handleStraightRenewal = () => {
    createStraightRenewal.mutate(
      {
        enrollmentEventId,
      },
      {
        onSuccess: async () => {
          if (isPlanOffThatchOrMedicare) {
            navigateToSpecificStep?.('medical_plan')
          } else {
            const refreshedData = await refreshOnboardingInfo()
            // The typing here is wrong from tanstack query so we need to send refreshedData! instead of refreshedData
            navigateToNextStep(refreshedData!)
          }
        },
        onError: (error) => {
          const errorMessage = (
            <OnboardingGenericErrorMessage error={error as GrapeAPIErrorType} />
          )
          setSubmitError(errorMessage)
        },
      }
    )
  }

  // Buttons
  function renderButtons () {
    const declineButton = (
      <TextButton
        onClick={() => {
          navigateToSpecificStep?.('opted_out')
        }}
      >
        Decline coverage
      </TextButton>
    )

    const previousStepButton = (
      <Button
        variant='secondary'
        onClick={() => {
          navigateToPreviousStep?.()
        }}
      >
        Back
      </Button>
    )

    const seeBudgetChangesButton = (
      <Button
        variant='secondary'
        onClick={() => {
          setShowPlanDetails(false)
        }}
      >
        Back
      </Button>
    )

    let exploreNewPlanLabel = 'Explore new plans'
    if (previousPlanUnavailable) {
      exploreNewPlanLabel = showGoThroughMedicareWarning
        ? 'Get medicare'
        : 'Select new plans'
    } else {
      exploreNewPlanLabel = isMedicareEligible ? 'Next' : 'Explore new plans'
    }
    const showNextButton =
      !previousPlanUnavailable &&
      !isMedicareEligible &&
      !currentDentalIsNotAvailableForNextYear &&
      !currentVisionIsNotAvailableForNextYear

    // Determine if dependents have changed for QLE button ordering
    const currentParticipants = planChangeSummary?.current_plan_year.covered_participants || []
    const nextParticipants = planChangeSummary?.next_plan_year.covered_participants || []
    const addedParticipants = getAddedParticipantNames(currentParticipants, nextParticipants)
    const removedParticipants = getRemovedParticipantNames(currentParticipants, nextParticipants)
    const hasDependentsChanged = addedParticipants.length > 0 || removedParticipants.length > 0

    // For QLE flow: if dependents changed, "Keep current plans" is primary; otherwise "Explore new plans" is primary
    // For non-QLE flow: keep existing logic based on isCurrentPlanOffThatchMedical
    const keepCurrentPlansIsPrimary = shouldShowQlePlan
      ? hasDependentsChanged
      : !isCurrentPlanOffThatchMedical

    const exploreNewPlans = (
      <Button
        variant={
          !showNextButton || !keepCurrentPlansIsPrimary
            ? 'primary'
            : 'secondary'
        }
        rightIcon={previousPlanUnavailable ? ArrowRightIcon : undefined}
        onClick={() => {
          navigateToSpecificStep?.('medical_plan')
        }}
      >
        {exploreNewPlanLabel}
      </Button>
    )

    const nextButton = showNextButton
      ? (
        <Button
          variant={keepCurrentPlansIsPrimary ? 'primary' : 'secondary'}
          onClick={() => {
            handleStraightRenewal()
          }}
        >
          Keep current plans
        </Button>
        )
      : null

    if (isDesktop) {
      // Primary button goes on the right (last position)
      const planNavButtons = keepCurrentPlansIsPrimary
        ? (
          <>
            {exploreNewPlans}
            {nextButton}
          </>
          )
        : (
          <>
            {nextButton}
            {exploreNewPlans}
          </>
          )
      const secondaryButton = previousPlanUnavailable || shouldShowQlePlan
        ? previousStepButton
        : seeBudgetChangesButton

      if (isQLEFlow) {
        return (
          <div className='mt-10'>
            <QLEButtonLayout
              backButton={secondaryButton}
              rightButtons={[planNavButtons]}
              navigateToSpecificStep={navigateToSpecificStep}
            />
          </div>
        )
      }
      return (
        <div className='flex justify-between items-center mt-16'>
          {declineButton}
          <ButtonGroup size='large'>
            {secondaryButton}
            {planNavButtons}
          </ButtonGroup>
        </div>
      )
    }

    // Primary button goes on top (first position) on mobile
    const planNavButtonsMobile = keepCurrentPlansIsPrimary
      ? (
        <>
          {nextButton}
          {exploreNewPlans}
        </>
        )
      : (
        <>
          {exploreNewPlans}
          {nextButton}
        </>
        )
    const secondaryButton = previousPlanUnavailable || shouldShowQlePlan
      ? previousStepButton
      : seeBudgetChangesButton

    if (isQLEFlow) {
      return (
        <QLEButtonLayout
          backButton={secondaryButton}
          rightButtons={[planNavButtonsMobile]}
          navigateToSpecificStep={navigateToSpecificStep}
        />
      )
    }
    return (
      <div className='flex flex-col gap-4 mt-10'>
        <ButtonGroup size='large' layout='vertical'>
          {planNavButtonsMobile}
          {secondaryButton}
        </ButtonGroup>
        {declineButton}
      </div>
    )
  }

  if (isPlanChangeSummaryLoading || !planChangeSummary) {
    return (
      <div
        data-testid='OnboardingOrchestratorLoading'
        className='flex items-center justify-center h-[calc(100vh-400px)]'
      >
        <LoadingSpinnerCentered />
      </div>
    )
  }

  // Check if we should show the QLE current plan content instead
  const shouldShowQlePlan = shouldShowQleCurrentPlan({
    enrollmentEvent: onboardingData?.enrollment_event,
    planChangeSummary,
    onboardingData,
  })

  function renderPlanChangesButtons () {
    const enrollmentEvent = onboardingData?.enrollment_event
    const isRenewal = isEnrollmentEventRenewal(enrollmentEvent)
    const buttonText = isRenewal ? 'See plan changes' : 'See plans'

    const previousStepButton = (
      <Button
        variant='secondary'
        onClick={() => {
          navigateToPreviousStep?.()
        }}
      >
        Back
      </Button>
    )
    const seePlanChangesButton = (
      <Button
        variant='primary'
        onClick={() => {
          setShowPlanDetails(true)
        }}
      >
        {buttonText}
      </Button>
    )

    if (isQLEFlow) {
      return (
        <div className={isDesktop ? 'mt-10' : ''}>

          <QLEButtonLayout
            backButton={previousStepButton}
            rightButtons={[seePlanChangesButton]}
            navigateToSpecificStep={navigateToSpecificStep}
          />
        </div>
      )
    }

    if (isDesktop) {
      return (
        <div className='mt-10'>
          <ButtonGroup align='right' size='large'>
            {previousStepButton}
            {seePlanChangesButton}
          </ButtonGroup>
        </div>
      )
    }

    return (
      <ButtonGroup size='large' layout='vertical'>
        {seePlanChangesButton}
        {previousStepButton}
      </ButtonGroup>
    )
  }

  const { title, description } = getSummaryTitleAndDescription({ enrollmentEvent: onboardingData?.enrollment_event })

  return (
    <motion.div
      data-testid='RenewalShowSummaryPage'
      className='max-w-[44rem] pb-8'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      key={showPlanDetails ? 'plan-details' : 'budget-and-plan-changes'}
      ref={pageRef}
    >
      {(!showPlanDetails || previousPlanUnavailable) && !shouldShowQlePlan && (
        <div className='mb-10'>
          <h1 className='heading-lg text-gray-800 mb-3'>
            {title}
          </h1>
          <p className='body text-gray-500'>
            {description}
          </p>
        </div>
      )}
      {previousPlanUnavailable && (
        <MonthlyAllowanceSection
          condensed={false}
          planChanges={planChangeSummary}
          maxAmountSubunits={Math.max(
            planChangeSummary?.next_plan_year?.allowance_amount.subunits || 0,
            planChangeSummary?.current_plan_year?.allowance_amount.subunits || 0
          )}
          companyName={employerName}
        />
      )}

      {!previousPlanUnavailable && showPlanDetails && (
        <motion.div
          initial={{ opacity: 0, transform: 'translateY(-120%)' }}
          animate={{ opacity: 1, transform: 'translateY(0)' }}
          transition={{ delay: 0.25 }}
          className='sticky top-10 xs:top-0 pt-8 -mt-10 xs:-mt-24 mb-8 bg-white rounded-b-2xl'
        >
          <BudgetAndPlanChangesSection
            hasTieredAllowances={isMemberOnTieredAllowance || false}
            planChanges={planChangeSummary}
            qmeEnabled={qmeAllowed}
            companyName={employerName}
            enrollmentEvent={onboardingData?.enrollment_event}
            condensed
          />
        </motion.div>
      )}

      {!previousPlanUnavailable && !showPlanDetails && !shouldShowQlePlan && (
        <BudgetAndPlanChangesSection
          hasTieredAllowances={isMemberOnTieredAllowance || false}
          planChanges={planChangeSummary}
          qmeEnabled={qmeAllowed}
          companyName={employerName}
          enrollmentEvent={onboardingData?.enrollment_event}
          condensed={false}
        />
      )}

      {!shouldPlanDetailsBeVisible && !shouldShowQlePlan && renderPlanChangesButtons()}

      {previousPlanUnavailable && (
        <div
          data-testid='must-choose-new-plans'
          className='mt-14 mb-10 p-8 rounded-2xl bg-thatch-100'
        >
          <h2 className='mb-4 heading-sm text-gray-800'>
            Choose {showGoThroughMedicareWarning ? 'Medicare' : 'new plans'} by{' '}
            {getEnglishLanguageDateFromDateString(
              onboardingData?.enrollment_event?.end_date || ''
            )}
          </h2>
          <p className='mb-8 body text-gray-800'>
            {showGoThroughMedicareWarning && (
              <>
                Someone in your family is 65 or older, or will be in the next
                few months, which means it's time for Medicare.
              </>
            )}
            {!showGoThroughMedicareWarning &&
              getPreviousPlanArentAvailableMessage({
                addressHasChanged,
                onboardingData,
                planChangeSummary,
              })}
          </p>
          <Button
            variant='primary'
            rightIcon={ArrowRightIcon}
            size='large'
            onClick={() => {
              navigateToSpecificStep?.('medical_plan')
            }}
          >
            {showGoThroughMedicareWarning
              ? 'Get medicare'
              : 'Explore new plans'}
          </Button>
        </div>
      )}

      {shouldShowQlePlan && (
        <QLEShowCurrentPlan
          planChangeSummary={planChangeSummary}
          hasFamily={hasFamily}
          restrictions={onboardingData?.restrictions as MemberOnboardingRestrictions}
        />
      )}

      {shouldPlanDetailsBeVisible && !shouldShowQlePlan && (
        <PlanDetailsSection
          planChangeSummary={planChangeSummary}
          hasFamily={hasFamily}
          medicalPlanNoPlanMessage={medicalPlanNoPlanMessage}
          restrictions={
            onboardingData?.restrictions as MemberOnboardingRestrictions
          }
          enrollmentEvent={onboardingData?.enrollment_event}
        />
      )}

      {submitError && (
        <div className='mt-10'>
          <Banner
            title='A problem has occurred while processing your request'
            type='critical'
          >
            {submitError}
          </Banner>
        </div>
      )}
      {shouldPlanDetailsBeVisible || shouldShowQlePlan ? renderButtons() : null}
    </motion.div>
  )
}
